import React, { useMemo } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { KPISelector } from '../../atoms/KPISelector';
import { KPIOption } from 'src/features/Settings/types';
import { useCurrency } from 'src/hooks/useCurrency';
import { getCurrencyLabel } from 'src/utils/currency';
import { getDerivedKpiUnit } from 'src/features/Settings/utils/constants';

type KPIModel = {
  id: string;
  kpiSource: string;
  kpiMetric: string;
  kpiUnit: string;
};

type KPIConfigurationProps = {
  model: KPIModel;
  onUnitChange: (modelId: string, value: string) => void;
  onUnitReset: (modelId: string) => void;
  onSourceChange: (modelId: string, value: string) => Promise<void>;
  onSourceReset: (modelId: string) => void;
  onMetricChange: (modelId: string, value: string) => void;
  onMetricReset: (modelId: string) => void;
  kpiSources: KPIOption[];
  kpiMetrics: KPIOption[];
  isLoading: boolean;
};

export const KPIConfiguration: React.FC<KPIConfigurationProps> = ({
  model,
  onSourceChange,
  onSourceReset,
  onMetricChange,
  onMetricReset,
  onUnitChange,
  onUnitReset,
  kpiSources,
  kpiMetrics,
  isLoading,
}) => {
  const { code: currencyCode } = useCurrency();
  // 'euro' is the internal token for "monetary" — the label shows the
  // customer's actual currency, e.g. "Swedish Krona (kr)".
  // For connector metrics with known semantics (Shopify) both units stay
  // visible but only the matching one is selectable — the user still
  // actively picks it, but can't pick wrong
  const kpiUnitOptions = useMemo<KPIOption[]>(() => {
    const allOptions: KPIOption[] = [
      { value: 'euro', label: getCurrencyLabel(currencyCode) },
      { value: 'unit', label: 'Unit' },
    ];
    const derivedUnit = getDerivedKpiUnit(model.kpiSource, model.kpiMetric);
    if (!derivedUnit) {
      return allOptions;
    }
    return allOptions.map((option) => ({
      ...option,
      disabled: option.value !== derivedUnit,
    }));
  }, [currencyCode, model.kpiSource, model.kpiMetric]);
  return (
    <Box
      border="1px solid"
      borderColor="surface.border"
      borderRadius="24px"
      p={6}
      bg="surface.panel">
      <Text fontSize="16px" fontWeight={600} color="content.title" mb={6}>
        KPI
      </Text>

      <Flex direction={{ base: 'column', lg: 'row' }} gap={4}>
        {/* Source Selector */}
        <KPISelector
          label="Source"
          value={model.kpiSource}
          onChange={(value) => onSourceChange(model.id, value)}
          onReset={() => onSourceReset(model.id)}
          options={kpiSources}
          placeholder="Select source"
          disabled={false}
          isLoading={isLoading}
        />

        {/* Metric Selector */}
        <KPISelector
          label="Metric"
          value={model.kpiMetric}
          onChange={(value) => onMetricChange(model.id, value)}
          onReset={() => onMetricReset(model.id)}
          options={kpiMetrics}
          placeholder="Select metric"
          disabled={!model.kpiSource || isLoading}
          isLoading={isLoading}
        />

        {/* KPI Unit Selector */}
        <KPISelector
          label="Unit"
          value={model.kpiUnit}
          onChange={(value) => onUnitChange(model.id, value)}
          onReset={() => onUnitReset(model.id)}
          options={kpiUnitOptions}
          placeholder="Select unit"
          disabled={!model.kpiSource || isLoading}
          isLoading={isLoading}
        />
      </Flex>
    </Box>
  );
};
